import { useState, useEffect, useCallback } from "react";
import { Wallet, Send, Euro, Calendar, MessageSquare, CheckCircle2, XCircle, Clock, Plus, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/config";

const STATUS_META = {
  pending: { label: "En attente", cls: "bg-amber-100 text-amber-800", Icon: Clock, dot: "bg-amber-500" },
  approved: { label: "Approuvée", cls: "bg-green-100 text-green-800", Icon: CheckCircle2, dot: "bg-green-500" },
  rejected: { label: "Refusée", cls: "bg-red-100 text-red-800", Icon: XCircle, dot: "bg-red-500" },
};

const EMPTY = { amount: "", reason: "", requested_date: "" };

export default function AcompteScreen() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/me/acompte-requests`, { withCredentials: true });
      setItems(r.data || []);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const submit = async () => {
    const amount = Number(form.amount);
    if (!amount || amount <= 0) { toast.error("Montant invalide"); return; }
    if (amount > 5000) { toast.error("Montant maximum : 5000 €"); return; }
    if (!form.reason.trim()) { toast.error("Merci d'indiquer un motif"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/me/acompte-requests`, {
        amount,
        reason: form.reason.trim(),
        requested_date: form.requested_date || null,
      }, { withCredentials: true });
      toast.success("Demande envoyée à votre agence");
      setOpen(false);
      setForm(EMPTY);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  const pending = items.filter((i) => i.status === "pending").length;

  return (
    <div className="p-4 space-y-4" data-testid="acompte-screen">
      <div className="flex items-center gap-2">
        <Wallet className="w-5 h-5 text-[#0069B4]" />
        <h1 className="text-lg font-bold text-[#3F3F3F]">Mes demandes d&apos;acompte</h1>
        {pending > 0 && (
          <Badge className="rounded-none bg-amber-100 text-amber-800 ml-1" data-testid="acompte-pending-count">{pending} en attente</Badge>
        )}
      </div>

      <Button
        onClick={() => { setForm(EMPTY); setOpen(true); }}
        className="w-full rounded-none bg-gradient-to-r from-[#662483] to-[#0069B4] text-white font-semibold h-11"
        data-testid="acompte-new-btn"
      >
        <Plus className="w-4 h-4 mr-2" /> Nouvelle demande d&apos;acompte
      </Button>

      {loading && <p className="text-xs text-gray-500 italic">Chargement…</p>}
      {!loading && items.length === 0 && (
        <div className="text-center py-8 text-sm text-gray-500 border border-dashed border-gray-200 bg-white" data-testid="acompte-empty">
          <Wallet className="w-8 h-8 mx-auto text-gray-300 mb-2" />
          Aucune demande pour l&apos;instant.
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-2" data-testid="acompte-list">
          {items.map((it) => {
            const meta = STATUS_META[it.status] || STATUS_META.pending;
            const created = new Date(it.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
            return (
              <div
                key={it.request_id}
                className={`bg-white border-l-4 ${meta.dot.replace("bg-", "border-")} border border-gray-200 p-3`}
                data-testid={`acompte-card-${it.request_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-bold text-[#3F3F3F]"><Euro className="w-4 h-4 inline mr-1 text-[#0069B4]" />{it.amount.toFixed(2)} €</p>
                    <p className="text-[11px] text-gray-500">Demandé le {created}{it.requested_date ? ` · souhaité pour le ${new Date(it.requested_date).toLocaleDateString("fr-FR")}` : ""}</p>
                  </div>
                  <Badge className={`rounded-none text-[10px] ${meta.cls}`} data-testid={`acompte-status-${it.request_id}`}>
                    <meta.Icon className="w-2.5 h-2.5 inline mr-0.5" /> {meta.label}
                  </Badge>
                </div>
                {it.reason && (
                  <div className="mt-2 text-[11px] text-gray-600 bg-[#FAFAFA] px-2 py-1 flex gap-1">
                    <MessageSquare className="w-3 h-3 text-gray-400 mt-0.5 shrink-0" />
                    <span>{it.reason}</span>
                  </div>
                )}
                {it.admin_comment && (
                  <div className={`mt-2 text-[11px] px-2 py-1 border-l-2 ${it.status === "approved" ? "bg-green-50 border-green-500 text-green-800" : "bg-red-50 border-red-500 text-red-800"}`} data-testid={`acompte-admin-comment-${it.request_id}`}>
                    <strong>Commentaire admin :</strong> {it.admin_comment}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* New request dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-full sm:max-w-md" data-testid="acompte-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#3F3F3F] flex items-center gap-2">
              <Wallet className="w-4 h-4 text-[#0069B4]" />
              Nouvelle demande d&apos;acompte
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            <p className="text-xs text-gray-500">Votre demande sera envoyée par email à votre agence de rattachement. Vous serez notifié dès qu&apos;elle sera traitée.</p>
            <div>
              <Label className="text-xs font-semibold text-gray-700 mb-1 block">Montant demandé (€) *</Label>
              <Input
                type="number"
                step="0.01"
                min="1"
                max="5000"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="ex. 300"
                className="rounded-none text-sm"
                data-testid="acompte-form-amount"
              />
              <p className="text-[10px] text-gray-400 mt-0.5">Maximum 5000 € par demande</p>
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700 mb-1 block">Motif *</Label>
              <Textarea
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                rows={3}
                placeholder="ex. Dépenses imprévues, avance sur salaire pour…"
                className="rounded-none text-sm"
                data-testid="acompte-form-reason"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700 mb-1 block flex items-center gap-1"><Calendar className="w-3 h-3" /> Date souhaitée (facultatif)</Label>
              <Input
                type="date"
                value={form.requested_date}
                onChange={(e) => setForm({ ...form, requested_date: e.target.value })}
                className="rounded-none text-sm"
                data-testid="acompte-form-date"
                min={new Date().toISOString().slice(0, 10)}
              />
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                onClick={submit}
                disabled={submitting}
                className="flex-1 rounded-none bg-gradient-to-r from-[#662483] to-[#0069B4] text-white font-semibold"
                data-testid="acompte-form-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {submitting ? "Envoi..." : "Envoyer la demande"}
              </Button>
              <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting} className="rounded-none" data-testid="acompte-form-cancel">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
